import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import api, { assetUrl, API_BASE } from "@/lib/api";
import { fileToDrawingDataUrl } from "@/lib/pdf";
import TakeoffStudio from "@/components/takeoff/TakeoffStudio";
import { AccountedPanel } from "@/components/takeoff/AccountedPanel";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PresenceStack from "@/components/takeoff/PresenceStack";
import {
  Upload, FileImage, X, ChevronLeft, ChevronRight, ChevronDown, Loader2, FolderOpen, Search,
  PanelLeftClose, PanelLeftOpen, Maximize2, Minimize2,
} from "lucide-react";

export default function TakeoffTab({ bid, setBid, readOnly, onSendToEstimate, updateBid, recompute, openDrawingId, locateTerm, flashMarkupId, computed, taskTypes, onGoToEstimate, onActiveDrawing, fullscreen, onToggleFullscreen }) {
  const drawings = bid.drawings || [];
  const { user } = useAuth();
  const [peers, setPeers] = useState([]);
  const [sheets, setSheets] = useState(null);       // all parsed sheets across every document
  const [docs, setDocs] = useState([]);             // packets (documents)
  const [folders, setFolders] = useState([]);       // folder tree (mirrors the Documents tab)
  const [activeKey, setActiveKey] = useState(null);  // "s:<sheet_id>" | "d:<drawing_id>"
  const [activeDrawing, setActiveDrawing] = useState(null);
  const [acctOpen, setAcctOpen] = useState(false);
  const [flashMarkupLocal, setFlashMarkupLocal] = useState(null); // ledger cross-sheet jump → flash a markup once its sheet mounts
  const [materializing, setMaterializing] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [treeInit, setTreeInit] = useState(false);
  const revealedOnce = useRef(false);
  const [q, setQ] = useState("");   // jump-to / filter query
  const [railOpen, setRailOpen] = useState(() => { const v = localStorage.getItem("takeoffRailOpen"); return v === null ? false : v === "1"; });
  useEffect(() => { localStorage.setItem("takeoffRailOpen", railOpen ? "1" : "0"); }, [railOpen]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [markups, setMarkups] = useState([]);   // all markups across the bid → sheet takeoff indicators

  const loadMarkups = useCallback(() => api.get(`/bids/${bid.bid_id}/markups`).then((r) => setMarkups(r.data || [])).catch(() => {}), [bid.bid_id]);

  const loadSheets = useCallback(() => {
    api.post(`/bids/${bid.bid_id}/warm-thumbs`).catch(() => {});  // backfill thumbnails in the background
    Promise.all([
      api.get(`/bids/${bid.bid_id}/sheets`),
      api.get(`/bids/${bid.bid_id}/documents`),
      api.get(`/bids/${bid.bid_id}/folders`),
    ]).then(([s, d, f]) => { setSheets(s.data || []); setDocs(d.data || []); setFolders(f.data || []); })
      .catch(() => { setSheets([]); setDocs([]); setFolders([]); });
  }, [bid.bid_id]);
  useEffect(() => { loadSheets(); }, [loadSheets]);

  // Keep the sheet-list takeoff indicators fresh (own + teammates'): poll every 8s.
  useEffect(() => { loadMarkups(); const t = setInterval(loadMarkups, 8000); return () => clearInterval(t); }, [loadMarkups]);
  // Refresh promptly when switching sheets so a just-drawn takeoff lights up its row.
  useEffect(() => { if (activeDrawing?.drawing_id) loadMarkups(); }, [activeDrawing?.drawing_id, loadMarkups]);

  // Live presence: heartbeat that we're viewing the active sheet, and poll who else is here.
  useEffect(() => {
    if (!bid.bid_id) return;
    const did = activeDrawing?.drawing_id;
    let alive = true;
    const beat = async () => {
      try {
        await api.post(`/bids/${bid.bid_id}/presence`, { drawing_id: did, sheet_id: activeDrawing?.sheet_id || null, drawing_name: activeDrawing?.name || null });
        const { data } = await api.get(`/bids/${bid.bid_id}/presence`);
        if (alive) setPeers(data || []);
      } catch { /* ignore */ }
    };
    beat();
    const t = setInterval(beat, 2500);
    return () => { alive = false; clearInterval(t); };
  }, [bid.bid_id, activeDrawing?.drawing_id]);

  // packets: each document with its sheets (ordered by page), plus an "Uploads" group
  const packets = useMemo(() => {
    const sheetIds = new Set((sheets || []).map((s) => s.sheet_id));
    // ad-hoc = direct uploads, plus any drawing whose backing sheet no longer exists (orphans)
    const adhoc = (bid.drawings || []).filter((d) => d.source !== "sheet" || !sheetIds.has(d.sheet_id));
    const byDoc = {};
    (sheets || []).forEach((s) => { (byDoc[s.document_id] ||= []).push(s); });
    const groups = docs
      .filter((d) => byDoc[d.document_id]?.length)
      .map((d) => ({
        id: d.document_id, name: d.name, folder_id: d.folder_id || null,
        items: byDoc[d.document_id].sort((a, b) => (a.page_number || 0) - (b.page_number || 0))
          .map((s) => ({ key: `s:${s.sheet_id}`, kind: "sheet", sheet: s, name: s.name, number: s.sheet_number })),
      }));
    if (adhoc.length) {
      groups.push({ id: "__uploads", name: "Uploads", folder_id: null, items: adhoc.map((d) => ({ key: `d:${d.drawing_id}`, kind: "drawing", drawing: d, name: d.name })) });
    }
    return groups;
  }, [sheets, docs, bid.drawings]);

  const flat = useMemo(() => packets.flatMap((g) => g.items), [packets]);

  // drawing_id for a rail item (sheets materialize lazily; ad-hoc uploads carry the drawing directly)
  const drawingIdFor = useCallback((it) => it.kind === "drawing" ? it.drawing?.drawing_id
    : (bid.drawings || []).find((d) => d.sheet_id === it.sheet.sheet_id)?.drawing_id, [bid.drawings]);
  // per-drawing takeoff summary → count + the distinct layer colors present on that sheet (for the rail bar)
  const markInfoByDrawing = useMemo(() => {
    const m = {};
    (markups || []).forEach((mk) => {
      if (!mk.drawing_id) return;
      const info = (m[mk.drawing_id] ||= { count: 0, colors: [] });
      info.count += 1;
      const col = mk.color || "#003E87";
      if (!info.colors.includes(col)) info.colors.push(col);
    });
    return m;
  }, [markups]);

  // Folder tree mirroring the Documents tab: folders by parent, packets by folder, and which folders hold sheets.
  const tree = useMemo(() => {
    const fbp = {}; (folders || []).forEach((f) => { (fbp[f.parent_id || "__root"] ||= []).push(f); });
    Object.values(fbp).forEach((a) => a.sort((x, y) => (x.name || "").localeCompare(y.name || "")));
    const pbf = {}; packets.forEach((g) => { (pbf[g.folder_id || "__root"] ||= []).push(g); });
    const memo = {};
    const has = (fid) => {
      if (fid in memo) return memo[fid];
      memo[fid] = false;
      const v = (pbf[fid] || []).length > 0 || (fbp[fid] || []).some((f) => has(f.folder_id));
      memo[fid] = v; return v;
    };
    return { fbp, pbf, has };
  }, [folders, packets]);
  const activeIdx = flat.findIndex((it) => it.key === activeKey);

  // materialize the drawing (with measurements/scale) for the active item
  const openItem = useCallback(async (item) => {
    if (!item) return;
    setActiveKey(item.key);
    if (item.kind === "drawing") { setActiveDrawing(item.drawing); return; }
    const existing = (bid.drawings || []).find((d) => d.sheet_id === item.sheet.sheet_id);
    if (existing) { setActiveDrawing(existing); return; }
    setMaterializing(true);
    try {
      const res = await api.post(`/bids/${bid.bid_id}/sheets/${item.sheet.sheet_id}/to-takeoff`);
      setBid((b) => {
        const has = (b.drawings || []).some((d) => d.drawing_id === res.data.drawing_id);
        return { ...b, drawings: has ? b.drawings : [...(b.drawings || []), res.data] };
      });
      setActiveDrawing(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open that sheet");
    } finally { setMaterializing(false); }
  }, [bid.bid_id, bid.drawings, setBid]);

  // pick initial / requested sheet once the list is ready — restore the last sheet for this project
  useEffect(() => {
    if (activeKey || flat.length === 0) return;
    if (openDrawingId) {
      const dw = drawings.find((d) => d.drawing_id === openDrawingId);
      const target = dw?.sheet_id ? flat.find((it) => it.kind === "sheet" && it.sheet.sheet_id === dw.sheet_id)
        : flat.find((it) => it.key === `d:${openDrawingId}`);
      if (target) { openItem(target); return; }
    }
    const saved = localStorage.getItem(`takeoffLastSheet:${bid.bid_id}`);
    const remembered = saved && flat.find((it) => it.key === saved);
    openItem(remembered || flat[0]);
  }, [flat, activeKey, openDrawingId, drawings, openItem, bid.bid_id]);

  // remember the last-opened sheet per project so we return here next time
  useEffect(() => { if (activeKey) localStorage.setItem(`takeoffLastSheet:${bid.bid_id}`, activeKey); }, [activeKey, bid.bid_id]);

  // Surface the active drawing up to the workspace so "pick up where you left off" can deep-link
  // straight back to THIS sheet — even when it was auto-loaded on entry, not just manually clicked.
  useEffect(() => { onActiveDrawing?.(activeDrawing); }, [activeDrawing?.drawing_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // default the whole tree to collapsed (dig in as you like)
  useEffect(() => {
    if (treeInit || packets.length === 0) return;
    const next = {};
    (folders || []).forEach((f) => { next[f.folder_id] = true; });
    packets.forEach((g) => { next[g.id] = true; });
    setCollapsed(next);
    setTreeInit(true);
  }, [packets, folders, treeInit]);

  // reveal only the path to the initially-opened sheet, once, so you can see where you are
  useEffect(() => {
    if (!treeInit || !activeKey || revealedOnce.current) return;
    const g = packets.find((grp) => grp.items.some((it) => it.key === activeKey));
    if (!g) return;
    const byId = Object.fromEntries((folders || []).map((f) => [f.folder_id, f]));
    setCollapsed((c) => {
      const n = { ...c, [g.id]: false };
      let fid = g.folder_id;
      while (fid) { n[fid] = false; fid = byId[fid]?.parent_id || null; }
      return n;
    });
    revealedOnce.current = true;
  }, [treeInit, activeKey, packets, folders]);

  // respond to an external "open this drawing" request
  useEffect(() => {
    if (!openDrawingId || flat.length === 0) return;
    const dw = drawings.find((d) => d.drawing_id === openDrawingId);
    const target = dw?.sheet_id ? flat.find((it) => it.kind === "sheet" && it.sheet.sheet_id === dw.sheet_id)
      : flat.find((it) => it.key === `d:${openDrawingId}`);
    if (target && target.key !== activeKey) openItem(target);
  }, [openDrawingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (delta) => { const n = activeIdx + delta; if (n >= 0 && n < flat.length) openItem(flat[n]); };
  const jumpToDrawing = useCallback((drawing_id) => {
    const dw = (bid.drawings || []).find((d) => d.drawing_id === drawing_id);
    const target = dw?.sheet_id ? flat.find((it) => it.kind === "sheet" && it.sheet.sheet_id === dw.sheet_id) : flat.find((it) => it.key === `d:${drawing_id}`);
    if (target) openItem(target);
  }, [bid.drawings, flat, openItem]);
  // Jump to a specific annotation on another sheet (from the ledger): open the sheet, then flash it.
  const jumpToMarkup = useCallback((drawing_id, markup_id) => {
    setFlashMarkupLocal(markup_id);
    jumpToDrawing(drawing_id);
    setTimeout(() => setFlashMarkupLocal(null), 4000);
  }, [jumpToDrawing]);

  // Deep-link / @mention notification: when an annotation link sets flashMarkupId, open THAT
  // markup's sheet (resolved from the all-sheets markups list) and flash it — never land on sheet 1.
  const flashedExternalRef = useRef(null);
  useEffect(() => {
    if (!flashMarkupId || flat.length === 0 || !(markups && markups.length)) return;
    if (flashedExternalRef.current === flashMarkupId) return;
    const mk = markups.find((m) => m.markup_id === flashMarkupId);
    if (!mk || !mk.drawing_id) return;
    flashedExternalRef.current = flashMarkupId;
    setFlashMarkupLocal(flashMarkupId);
    if (activeDrawing?.drawing_id !== mk.drawing_id) jumpToDrawing(mk.drawing_id);
    setTimeout(() => setFlashMarkupLocal(null), 4000);
  }, [flashMarkupId, markups, flat.length, activeDrawing?.drawing_id, jumpToDrawing]);

  // Left / Right arrow keys flip between sheets (ignored while typing or with modifiers).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      const n = activeIdx + (e.key === "ArrowRight" ? 1 : -1);
      if (n >= 0 && n < flat.length) { e.preventDefault(); openItem(flat[n]); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeIdx, flat, openItem]);

  // Presence deep-link: jump to the sheet a teammate is currently viewing.
  const jumpToPeer = useCallback((peer) => {
    if (!peer || peer.drawing_id === activeDrawing?.drawing_id) return;
    const target = peer.sheet_id
      ? flat.find((it) => it.kind === "sheet" && it.sheet.sheet_id === peer.sheet_id)
      : flat.find((it) => it.key === `d:${peer.drawing_id}`);
    if (target) openItem(target);
    else if (peer.drawing_id) jumpToDrawing(peer.drawing_id);
  }, [flat, activeDrawing?.drawing_id, openItem, jumpToDrawing]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await fileToDrawingDataUrl(file);
      const dpi = file.type === "application/pdf" ? 144 : null;
      const res = await api.post(`/bids/${bid.bid_id}/drawings`, { name: file.name, data_url: dataUrl, dpi });
      setBid((b) => ({ ...b, drawings: [...(b.drawings || []), res.data] }));
      setActiveKey(`d:${res.data.drawing_id}`); setActiveDrawing(res.data);
      toast.success("Sheet loaded");
    } catch { toast.error("Could not load that file. Use a PDF or image."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const controls = !readOnly && (
    <div className="flex gap-2">
      <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={upload} data-testid="drawing-upload-input" />
      <Button variant="outline" size="sm" className="text-xs" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="drawing-upload-button">
        <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? "Loading…" : "Upload"}
      </Button>
    </div>
  );

  if (sheets === null) {
    return <div className="h-full flex items-center justify-center text-slate-400 gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading sheets…</div>;
  }

  if (flat.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 text-center px-6">
        <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center"><FolderOpen className="w-6 h-6 text-slate-400" /></div>
        <div>
          <div className="text-sm font-medium text-slate-800">No sheets yet</div>
          <div className="text-xs text-slate-500 mt-1">Upload PDFs, images, Word, Excel or text files in the <b>Documents</b> tab — every page appears here automatically, grouped by packet.</div>
        </div>
        {controls}
      </div>
    );
  }

  const active = flat[activeIdx];
  const activePeers = peers
    .filter((p) => p.user_id !== user?.user_id)
    .map((p) => ({ ...p, here: p.drawing_id === activeDrawing?.drawing_id }))
    .sort((a, b) => (b.here ? 1 : 0) - (a.here ? 1 : 0));

  const rowThumb = (it) => it.kind === "sheet"
    ? `${API_BASE}/sheets/${it.sheet.sheet_id}/image?thumb=1`
    : (it.drawing?.data_url || null);

  const SheetRow = (it) => {
    const did = drawingIdFor(it);
    const info = did ? markInfoByDrawing[did] : null;
    const has = !!(info && info.count);
    return (
      <button key={it.key} onClick={() => openItem(it)} data-testid={`sheet-item-${it.key}`}
        className={`relative w-full flex items-center gap-2 px-2 py-1.5 pb-2 text-left transition ${activeKey === it.key ? "bg-[#003E87]/10 border-l-2 border-[#003E87]" : "border-l-2 border-transparent hover:bg-slate-50"}`}>
        <span className="w-12 h-9 rounded border border-slate-200 bg-slate-50 overflow-hidden shrink-0 flex items-center justify-center">
          {rowThumb(it) ? <img src={rowThumb(it)} alt="" loading="lazy" className="w-full h-full object-cover" /> : <FileImage className="w-4 h-4 text-slate-300" />}
        </span>
        <span className="min-w-0 flex-1">
          {it.number ? <div className="text-[11px] font-mono text-[#003E87] truncate">{it.number}</div> : null}
          <div className="text-xs text-slate-700 truncate">{it.name}</div>
        </span>
        {has && (
          <span title={`${info.count} takeoff${info.count === 1 ? "" : "s"} on this sheet`} data-testid={`sheet-takeoff-count-${it.key}`}
            className="shrink-0 text-[10px] font-bold text-[#003E87] bg-[#003E87]/10 rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center">{info.count}</span>
        )}
        {has && (
          <span className="absolute left-0 right-0 bottom-0 flex h-[3px] overflow-hidden" data-testid={`sheet-takeoff-bar-${it.key}`}>
            {info.colors.slice(0, 6).map((c, i) => <span key={i} className="flex-1" style={{ background: c }} />)}
          </span>
        )}
      </button>
    );
  };

  const nq = q.trim().toLowerCase();
  const filtered = nq ? flat.filter((it) => (it.number || "").toLowerCase().includes(nq) || (it.name || "").toLowerCase().includes(nq)) : null;
  const onSearchKey = (e) => { if (e.key === "Enter" && filtered?.length) openItem(filtered[0]); };

  // Recursive folder → packet → sheet renderer (mirrors the Documents tab structure).
  const renderNode = (parentKey, depth) => (
    <>
      {(tree.fbp[parentKey] || []).filter((f) => tree.has(f.folder_id)).map((f) => {
        const isC = collapsed[f.folder_id];
        return (
          <div key={f.folder_id} className="border-b border-slate-50">
            <button onClick={() => setCollapsed((c) => ({ ...c, [f.folder_id]: !c[f.folder_id] }))}
              style={{ paddingLeft: depth * 12 + 12 }}
              className="w-full flex items-center gap-1.5 pr-3 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50" data-testid={`takeoff-folder-${f.folder_id}`}>
              <ChevronDown className={`w-3.5 h-3.5 transition ${isC ? "-rotate-90" : ""}`} />
              <FolderOpen className="w-3.5 h-3.5 text-[#003E87] shrink-0" />
              <span className="truncate flex-1 text-left">{f.name}</span>
            </button>
            {!isC && renderNode(f.folder_id, depth + 1)}
          </div>
        );
      })}
      {(tree.pbf[parentKey] || []).map((g) => {
        const isC = collapsed[g.id];
        const pktHas = g.items.some((it) => { const d = drawingIdFor(it); return d && markInfoByDrawing[d]?.count; });
        return (
          <div key={g.id} className="border-b border-slate-100">
            <button onClick={() => setCollapsed((c) => ({ ...c, [g.id]: !c[g.id] }))}
              style={{ paddingLeft: depth * 12 + 12 }}
              className="w-full flex items-center gap-1.5 pr-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-50" data-testid={`packet-${g.id}`}>
              <ChevronDown className={`w-3.5 h-3.5 transition ${isC ? "-rotate-90" : ""}`} />
              <span className="truncate flex-1 text-left">{g.name}</span>
              {pktHas && <span title="Has takeoffs" data-testid={`packet-takeoff-dot-${g.id}`} className="w-1.5 h-1.5 rounded-full bg-[#F4C400] shrink-0" />}
              <span className="text-slate-300">{g.items.length}</span>
            </button>
            {!isC && <div className="pb-1">{g.items.map(SheetRow)}</div>}
          </div>
        );
      })}
    </>
  );

  return (
    <div className="flex h-full min-h-0">
      {/* Left rail — auto-populated packets → sheets, with thumbnails + jump search */}
      {!railOpen ? (
        <div className="w-10 shrink-0 border-r border-slate-200 bg-white flex flex-col items-center py-2 gap-2" data-testid="takeoff-sheet-rail-collapsed">
          <button onClick={() => setRailOpen(true)} data-testid="takeoff-rail-expand" title="Show sheet list" className="w-8 h-8 grid place-items-center rounded-md border border-slate-200 text-[#003E87] hover:bg-slate-50"><PanelLeftOpen className="w-4 h-4" /></button>
          <button onClick={() => setRailOpen(true)} title="Show sheet list" className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 [writing-mode:vertical-rl] rotate-180 py-1">Sheets · {flat.length}</button>
        </div>
      ) : (
      <div className="w-52 shrink-0 border-r border-slate-200 bg-white overflow-auto" data-testid="takeoff-sheet-rail">
        <div className="p-2 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-1.5">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey}
                placeholder="Jump to sheet…" data-testid="takeoff-sheet-search"
                className="w-full h-8 pl-7 pr-6 text-xs rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-[#003E87]" />
              {q && <button onClick={() => setQ("")} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="takeoff-search-clear"><X className="w-3.5 h-3.5" /></button>}
            </div>
            <button onClick={() => setRailOpen(false)} data-testid="takeoff-rail-collapse" title="Collapse sheet list" className="shrink-0 w-8 h-8 grid place-items-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"><PanelLeftClose className="w-4 h-4" /></button>
          </div>
        </div>
        {filtered ? (
          <div className="pb-1" data-testid="takeoff-search-results">
            {filtered.length === 0
              ? <div className="px-3 py-4 text-xs text-slate-400">No sheets match “{q}”.</div>
              : filtered.map(SheetRow)}
          </div>
        ) : (
          renderNode("__root", 0)
        )}
      </div>
      )}

      {/* Canvas + Procore-style navigation header */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-white" data-testid="takeoff-nav">
          <Button variant="outline" size="sm" className="h-8 px-2" disabled={activeIdx <= 0} onClick={() => go(-1)} data-testid="takeoff-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0 flex-1 text-center">
            <div className="text-sm font-medium text-slate-800 truncate">
              {active?.number ? <span className="font-mono text-[#003E87] mr-1.5">{active.number}</span> : null}{active?.name}
            </div>
            <div className="text-[11px] text-slate-400">Sheet {activeIdx + 1} of {flat.length}</div>
          </div>
          <Button variant="outline" size="sm" className="h-8 px-2" disabled={activeIdx >= flat.length - 1} onClick={() => go(1)} data-testid="takeoff-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {activePeers.length > 0 && <PresenceStack peers={activePeers} onJump={jumpToPeer} />}
          {controls && <div className="ml-1">{controls}</div>}
          <Button variant="outline" size="sm" className="h-8 px-2 text-xs" disabled={!activeDrawing} onClick={() => setAcctOpen(true)} data-testid="takeoff-accounted-btn"
            title="What is accounted for on this drawing: layers, quantities, the lines they feed, scale and notes">Accounted for</Button>
          {onToggleFullscreen && (
            <Button variant={fullscreen ? "default" : "outline"} size="sm"
              className={`h-8 px-2 ${fullscreen ? "bg-[#003E87] hover:bg-[#00305F] text-white" : ""}`}
              onClick={onToggleFullscreen} data-testid="takeoff-fullscreen-toggle"
              title={fullscreen ? "Exit full screen" : "Full screen — hide everything but the drawing & tools"}>
              {fullscreen ? <><Minimize2 className="w-4 h-4 mr-1" /> Exit full screen</> : <Maximize2 className="w-4 h-4" />}
            </Button>
          )}
        </div>
        <div className="flex-1 min-w-0 min-h-0 relative">
          {materializing && <div className="absolute inset-0 z-10 bg-white/60 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#003E87]" /></div>}
          {activeDrawing ? (
            <TakeoffStudio key={activeDrawing.drawing_id} bid={bid} drawing={activeDrawing} readOnly={readOnly} flashMarkupId={flashMarkupId || flashMarkupLocal} computed={computed}
              onReload={() => api.get(`/bids/${bid.bid_id}`).then((r) => setBid(r.data)).catch(() => {})}
              onSendToEstimate={onSendToEstimate} updateBid={updateBid} locate={locateTerm ? { term: locateTerm } : null} onJumpToDrawing={jumpToDrawing} onJumpToMarkup={jumpToMarkup}
              onCalibrated={() => api.get(`/bids/${bid.bid_id}`).then((r) => setBid(r.data)).catch(() => {})}
              onScaleSaved={(s) => {
                api.put(`/bids/${bid.bid_id}/drawings/${activeDrawing.drawing_id}`, { scale: s }).then((r) => setBid(r.data)).catch(() => {});
                if (activeDrawing.sheet_id) api.put(`/bids/${bid.bid_id}/sheets/${activeDrawing.sheet_id}`, { scale: s }).catch(() => {});
              }}
              onApplyScaleToPacket={async (s) => {
                try {
                  const { data } = await api.post(`/bids/${bid.bid_id}/drawings/${activeDrawing.drawing_id}/apply-scale-packet`, { scale: s });
                  const docId = (sheets || []).find((sh) => sh.sheet_id === activeDrawing.sheet_id)?.document_id;
                  const pkg = new Set((sheets || []).filter((sh) => sh.document_id === docId).map((sh) => sh.sheet_id));
                  setBid((b) => ({ ...b, drawings: (b.drawings || []).map((d) => (pkg.has(d.sheet_id) || d.drawing_id === activeDrawing.drawing_id) ? { ...d, scale: s } : d) }));
                  const cnt = data.sheets || data.drawings || 1;
                  toast.success(`Scale applied to ${cnt} sheet${cnt === 1 ? "" : "s"} in this packet`);
                } catch (e) { toast.error(e.response?.data?.detail || "Could not apply scale to packet"); }
              }} />
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm"><FileImage className="w-4 h-4 mr-1.5" /> Select a sheet to start</div>
          )}
        </div>
      </div>
      <AccountedPanel open={acctOpen} onOpenChange={setAcctOpen} bidId={bid.bid_id} drawingId={activeDrawing?.drawing_id} readOnly={readOnly}
        onChanged={() => api.get(`/bids/${bid.bid_id}`).then((r) => setBid(r.data)).catch(() => {})} />

    </div>
  );
}
